import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Loader2, MessageSquare, Sparkles, Calendar } from "lucide-react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";

function fmtRange(startIso, endIso) {
  const s = new Date(startIso);
  const e = new Date(endIso);
  // end is exclusive Monday → show Sunday of the week
  const sunday = new Date(e.getTime() - 86_400_000);
  const opts = { month: "short", day: "numeric" };
  const sameYear = s.getUTCFullYear() === sunday.getUTCFullYear();
  const start = s.toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
  const end = sunday.toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
  return sameYear
    ? `${start} – ${end}, ${sunday.getUTCFullYear()}`
    : `${start} ${s.getUTCFullYear()} – ${end} ${sunday.getUTCFullYear()}`;
}

export default function HallOfFamePage() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Hall of Fame — Weekly Champions | TorHub",
    description:
      "TorHub's Hall of Fame ranks past Users of the Week by the karma they earned during their winning week. A new champion is crowned every Monday — see who's posted the best comments, helpful reviews and most upvoted contributions to the community.",
    path: "/hall-of-fame",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/hall_of_fame", { params: { limit: 12 } });
        setWeeks(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-8 py-6 flex-1 max-w-4xl mx-auto w-full">
          <Link
            to="/"
            className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6"
            data-testid="hall-of-fame-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <header className="mb-8">
            <h1 className="font-display text-3xl md:text-4xl font-extrabold flex items-center gap-3">
              <Crown className="w-8 h-8 text-[var(--tt-yellow)]" />
              Hall of Fame
            </h1>
            <p className="text-sm text-[var(--tt-text-muted)] mt-3 leading-relaxed max-w-3xl">
              Every Monday at midnight UTC a new <strong className="text-white/80">User of the Week</strong> is crowned —
              chosen by the karma they earned across the previous seven days. Comments that the community upvoted, helpful
              reviews, smart picks and constructive moderation all add to a member&#39;s weekly score. The leaderboard below
              is the permanent record of those weekly champions, ranked by total karma earned during their winning week.
            </p>
            <p className="text-xs text-[var(--tt-text-faint)] mt-2 leading-relaxed max-w-3xl">
              Want to claim a crown? Hop into a title&apos;s discussion thread, share an honest take, or help a fellow user
              with codec / language questions. Quality contributions beat quantity — a single insightful post can outweigh
              a dozen one-word replies.
            </p>
          </header>

          {loading ? (
            <div className="flex items-center gap-2 text-[var(--tt-text-muted)] py-12">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading hall of fame…
            </div>
          ) : weeks.length === 0 ? (
            <div className="text-center text-[var(--tt-text-muted)] py-16 text-sm border border-[var(--tt-border)] rounded-2xl bg-[var(--tt-surface)]">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-[var(--tt-primary)]" />
              No champions yet. Be the first — post comments and earn upvotes to claim this week&#39;s crown.
            </div>
          ) : (
            <ol className="space-y-3" data-testid="hall-of-fame-list">
              {weeks.map((w, idx) => {
                const isCurrent = w.in_progress;
                const rank = isCurrent ? null : weeks.filter((x) => !x.in_progress).indexOf(w) + 1;
                return (
                  <li
                    key={w.week_start}
                    data-testid={`hall-of-fame-row-${idx}`}
                    className={`relative bg-[var(--tt-surface)] border rounded-2xl p-4 md:p-5 flex items-center gap-4 transition-colors ${
                      isCurrent
                        ? "border-[var(--tt-primary)]/60 shadow-[0_0_0_1px_rgba(255,42,109,0.2)]"
                        : "border-[var(--tt-border)] hover:border-[var(--tt-primary)]/40"
                    }`}
                  >
                    {/* Rank / crown */}
                    <div className="flex-shrink-0 w-12 text-center">
                      {isCurrent ? (
                        <Crown className="w-7 h-7 text-[var(--tt-yellow)] mx-auto" />
                      ) : rank === 1 ? (
                        <Crown className="w-7 h-7 text-[var(--tt-yellow)] mx-auto" />
                      ) : (
                        <div className="font-mono-tt text-xl text-[var(--tt-text-muted)]">
                          #{rank}
                        </div>
                      )}
                    </div>

                    {/* Avatar */}
                    {w.picture ? (
                      <img
                        src={w.picture}
                        alt={w.name}
                        className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover border border-[var(--tt-border)] flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-[var(--tt-primary)] via-[var(--tt-yellow)] to-[var(--tt-purple)] flex items-center justify-center text-2xl font-bold flex-shrink-0">
                        {(w.name?.[0] || "?").toUpperCase()}
                      </div>
                    )}

                    {/* Identity + week */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/u/${w.user_id}`}
                          data-testid={`hall-of-fame-name-${idx}`}
                          className="font-display text-lg md:text-xl font-bold hover:text-[var(--tt-primary)] truncate"
                        >
                          {w.name}
                        </Link>
                        {isCurrent && (
                          <span className="text-[9px] uppercase tracking-wider font-bold bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] border border-[var(--tt-primary)]/30 px-1.5 py-0.5 rounded">
                            In Progress
                          </span>
                        )}
                        {w.role === "admin" && (
                          <span className="text-[9px] uppercase font-bold bg-[var(--tt-yellow)] text-black px-1.5 py-0.5 rounded">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--tt-text-muted)] mt-1 flex items-center gap-1.5 font-mono-tt">
                        <Calendar className="w-3 h-3" />
                        {fmtRange(w.week_start, w.week_end)}
                      </div>
                    </div>

                    {/* Weekly stats */}
                    <div className="text-right flex-shrink-0">
                      <div className="font-display text-2xl md:text-3xl font-extrabold text-[var(--tt-primary)]">
                        +{w.weekly_karma}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">
                        karma
                      </div>
                      <div className="text-[11px] text-[var(--tt-text-muted)] mt-1 flex items-center gap-1 justify-end font-mono-tt">
                        <MessageSquare className="w-3 h-3" />
                        {w.weekly_comments} {w.weekly_comments === 1 ? "post" : "posts"}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
}
